import Flickity from 'flickity';

import { Panzoom } from '@fancyapps/ui/dist/panzoom/panzoom.esm.js';

const zoomOptions = {
  click: 'toggleMax',
  dblClick: false,
  maxScale: 2,
  panOnlyZoomed: true,
  wheel: 'false',
  bounce: false,
  panMode: 'mousemove',
};

let zoomInstances: any[] = [];
let currentInstance;

document.addEventListener('DOMContentLoaded', () => {
  const gallery = document.querySelector('.js-pdp-gallery');

  if (!gallery) {
    return;
  }

  const main = gallery.querySelector('.pdp-gallery__main');
  if (!main) {
    return;
  }
  const slides = main.querySelectorAll('.js-slide');
  const thumbnails = gallery.querySelector('.pdp-gallery__thumbs');
  const overlay = gallery?.querySelector('.pdp-gallery__overlay') as HTMLElement;
  const videoBtns = document.querySelectorAll<HTMLButtonElement>('.pdp-gallery__video-btn');
  const zoomBtn = gallery.querySelector<HTMLButtonElement>('.js-zoom-btn');

  const mainSlider = new Flickity(main, {
    cellSelector: '.js-slide',
    wrapAround: true,
    cellAlign: 'center',
    pageDots: false,
    prevNextButtons: false,
    draggable: false,
    selectedAttraction: 0.018,
    adaptiveHeight: false,
    autoPlay: false,
    on: {
      ready: initPanZoom,
      change: function(index: number) {
        currentInstance = zoomInstances[index];

        // we need to hide click to zoom/pan overlay on videos
        if (currentInstance) {
          overlay.classList.remove('pdp-gallery__overlay--hidden');
        } else {
          overlay.classList.add('pdp-gallery__overlay--hidden');
        }

        zoomInstances.forEach((instance) => {
          if (instance) {
            instance.reset();
          }
        });

        // pause all videos
        slides.forEach((slide, i) => {
          if (i !== index) {
            const video = slide.querySelector<HTMLVideoElement>('.pdp-gallery__video');
            if (video && !video.paused) {
              video.pause();
              video.currentTime = 0;
            }

            // reset button visibility
            const btn = slide.querySelector<HTMLElement>('.pdp-gallery__video-btn');
            if (btn) {
              btn.removeAttribute('aria-hidden');
              btn.style.display = '';
            }
          }
        });
      },
    },
  });

  if (thumbnails) {
    const thumbnailBtns = thumbnails.querySelectorAll('.pdp-gallery__thumb-btn');

    let prev = thumbnails.querySelector('.js-prev');
    let next = thumbnails.querySelector('.js-next');

    const thumbnailSlider = new Flickity(thumbnails, {
      cellSelector: '.js-slide',
      wrapAround: true,
      cellAlign: 'left',
      pageDots: false,
      prevNextButtons: false,
      draggable: true,
      selectedAttraction: 0.018,
      adaptiveHeight: false,
      autoPlay: false,
      contain: true,
      groupCells: false,
      // TODO: Do we want to disable a11y so it doesn't focus on the thumb container itself?
      // accessibility: false,
    });

    prev?.addEventListener('click', () => {
      thumbnailSlider.previous();
    });

    next?.addEventListener('click', () => {
      thumbnailSlider.next();
    });

    thumbnailBtns.forEach((btn, index) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();

        thumbnailBtns.forEach((button) => {
          button.setAttribute('aria-selected', 'false');
        });

        // Add aria-selected to the clicked button
        btn.setAttribute('aria-selected', 'true');

        mainSlider.select(index, true, false);
      });
    });
  }

  zoomBtn?.addEventListener('click', () => {
    if (!currentInstance) {
      return;
    }

    if (currentInstance.scale > 1) {
      currentInstance.reset();
    } else {
      currentInstance.panTo?.({ scale: 2, friction: 0.3 });
    }

    updateZoomButton();
  });

  slides?.forEach((_slide, index) => {
    const instance = zoomInstances[index];
    if (instance) {
      instance.on('afterTransform', () => {
        if (!zoomBtn) {
          return;
        }
        if (instance.scale > 1) {
          zoomBtn.classList.add('is-zoomed');
          zoomBtn.setAttribute('aria-label', 'Zoom out');
        } else {
          zoomBtn.classList.remove('is-zoomed');
          zoomBtn.setAttribute('aria-label', 'Zoom in');
        }
      });
    }
  });

  // Show helper overlay on first touch interaction, then hide shortly after
  if (window.matchMedia('(pointer: coarse)').matches) {
    let hasShown = false;

    const showOverlayOnce = () => {
      if (!hasShown) {
        overlay.classList.add('pdp-gallery__overlay--visible');
        hasShown = true;

        setTimeout(() => {
          overlay.classList.remove('pdp-gallery__overlay--visible');
        }, 4000);

        // remove listener after first trigger
        document.body.removeEventListener('touchstart', showOverlayOnce);
      }
    };

    document.body.addEventListener('touchstart', showOverlayOnce, { passive: true });
  }

  // Hide helper overlay after clicking on the gallery for the first time
  main?.addEventListener('click', () => {
    overlay?.setAttribute('aria-hidden', 'true');
  });

  // initiate panzoom

  function initPanZoom() {
    slides?.forEach((slide) => {
      const container = slide.querySelector('.f-panzoom');
      if (container) {
        const instance = new Panzoom(container, {
          ...zoomOptions,
          minScale: 1, // ensure it can zoom back out
        });
        zoomInstances.push(instance);

        instance.on('afterTransform', () => {
          // only update the UI if this instance is the active one
          if (currentInstance === instance) {
            updateZoomButton();
          }
        });
      } else {
        zoomInstances.push(null);
      }
    });

    currentInstance = zoomInstances[0] || null;
  }

  // revert zoom when clicking off slide
  document.addEventListener('click', function(event) {
    if (main && !main.contains(event.target as Node)) {
      if (currentInstance) {
        currentInstance.reset();
      } else {
        // Fallback: reset the first available Panzoom
        const firstInstance = zoomInstances.find((inst) => inst !== null);
        firstInstance?.reset();
      }
    }
  });

  // play videos

  videoBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();

      const btnElement = e.currentTarget as HTMLElement;

      const wrapper = btnElement.nextElementSibling as HTMLElement | null;
      const video = wrapper?.querySelector('video');

      if (!(video instanceof HTMLVideoElement)) {
        return;
      }

      video.setAttribute('tabindex', '-1'); // Ensure it's focusable (if not already)
      video.focus();

      video.play();

      btnElement.setAttribute('aria-hidden', 'true');
    });
  });

  function getCurrentScale(panzoomInstance: any) {
    if (!panzoomInstance) {
      return 1;
    }
    const tr = panzoomInstance.getTransform?.() ?? { scale: 1 };
    return tr.scale ?? 1;
  }

  function updateZoomButton() {
    if (!zoomBtn) {
      return;
    }

    const scale = getCurrentScale(currentInstance);
    if (scale > 1.01) {
      zoomBtn.classList.add('is-zoomed');
      zoomBtn.setAttribute('aria-label', 'Zoom out');
    } else {
      zoomBtn.classList.remove('is-zoomed');
      zoomBtn.setAttribute('aria-label', 'Zoom in');
    }
  }
});

// adds resize class for making slides same height
Flickity.prototype._createResizeClass = function() {
  this.element.classList.add('flickity-resize');
};

Flickity.create._createResizeClass = function() {
  this.element.classList.add('flickity-resize');
};

let resize = Flickity.prototype.resize;
Flickity.prototype.resize = function() {
  this.element.classList.remove('flickity-resize');
  resize.call(this);
  this.element.classList.add('flickity-resize');
};

// when slider images load we need to fire a resize to adjust slider

let sliderImages = document.querySelectorAll('.js-slide img');

sliderImages.forEach((img) => {
  img.addEventListener('load', () => {
    window.dispatchEvent(new Event('resize'));
  });
});
